// THE ONE WAY THIS LANDS WRONG — BE-Light's warning: they always serve the key and it is **`null`**
// when the user has never chosen — not omitted, consistent with every other preference key. A store
// checking `=== undefined` would read `null` as "set", and a truthiness check would read it as OFF
// turning the animation off for every user who never touched it.
import { create } from "zustand";
import { api } from "../api";
import { reportSwallowed } from "../lib/reportSwallowed";

const KEY = "sm_enhanced_visuals";

let serverHasKey = false;

/* * The whole rule, in one place. Only an explicit `false` is off. */
export function isEnabled(value: unknown): boolean {
  return value !== false;
}

function load(): boolean {
  try {
    const raw = localStorage.getItem(KEY);
    // absent = never chose = ON. Only an explicit "0" turns it off.
    return raw === null ? true : raw !== "0";
  } catch {
    return true;
  }
}

interface EnhancedVisualsState {
  enabled: boolean;
  set: (on: boolean) => void;
  toggle: () => void;
  hydrateFromServer: (
    prefs: Record<string, unknown> | null | undefined,
  ) => void;
}

export const useEnhancedVisuals = create<EnhancedVisualsState>((set, get) => ({
  enabled: load(),

  set: (on) => {
    set({ enabled: on });
    try {
      localStorage.setItem(KEY, on ? "1" : "0");
    } catch {
      /* private mode — the preference degrades to this session rather than throwing */
    }
    if (serverHasKey) {
      // A single-key PATCH, which BE-Light confirmed is valid and does not require the soil keys
      // turning this off is the only preference edit most users will ever make. A failed save
      // leaves the local value in place rather than reverting under the user's cursor.
      void api
        .updateSoilUnits({ enhanced_visuals: on } as never)
        .catch((err) => reportSwallowed("enhanced visuals preference", err));
    }
  },

  toggle: () => get().set(!get().enabled),

  hydrateFromServer: (prefs) => {
    if (!prefs || typeof prefs !== "object") return;
    if (!("enhanced_visuals" in prefs)) return;
    serverHasKey = true;
    const v = prefs.enhanced_visuals;
    // `null` = never chose. Fall back to whatever this browser holds, which may carry a choice made
    // before the server key existed — adopting the server's null would silently discard it.
    if (v == null) return;
    set({ enabled: isEnabled(v) });
  },
}));
